import { createFileRoute } from '@tanstack/react-router';
import { Badge } from '@/components/Badge';
import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { stacktraceNS, useTranslation } from '@/lib/i18n';
export const Route = createFileRoute('/_stacktrace/development-tickets/external-integrations/')({
  component: ExternalIntegrationsPage
});
type SyncResult = 'success' | 'partialError' | 'none';
type ExternalSystemIntegration = {
  id: 'jira' | 'linear' | 'notion' | 'redmine';
  name: string;
  iconName: IconName;
  tenantName: string | null;
  lastSyncedAt: string | null;
  lastSyncedAtRelative: string | null;
  syncType: string | null;
  syncResult: SyncResult;
  /**
   * 連携済みかどうか。
   * モックでは JIRA だけが連携済みとして表示される。
   * 未連携でも一覧上には表示し「連携する」ボタンから連携導線へ進める想定。
   */
  connected: boolean;
};

/**
 * 開発チケット追加時に同期可能な外部システム一覧（モック）。
 * 本来は API から取得する想定。デバッグ用途で JIRA のみ連携済みとして表示する。
 *
 * Issue 側 (`/issues/external-integrations`) と同じ shape にすることで、
 * 将来 API 化された際に共通フックを使い回せるようにしている。
 */
const EXTERNAL_SYSTEM_INTEGRATIONS: ExternalSystemIntegration[] = [{
  id: 'jira',
  name: 'JIRA',
  iconName: 'AlertCircle',
  tenantName: 'dresscode.atlassian.net',
  lastSyncedAt: '2025年11月17日 12時6分',
  lastSyncedAtRelative: '6か月前',
  syncType: '一方向インポート',
  syncResult: 'success',
  connected: true
}, {
  id: 'linear',
  name: 'Linear',
  iconName: 'LayersIcon',
  tenantName: null,
  lastSyncedAt: null,
  lastSyncedAtRelative: null,
  syncType: null,
  syncResult: 'none',
  connected: false
}, {
  id: 'notion',
  name: 'Notion',
  iconName: 'BookOpenIcon',
  tenantName: null,
  lastSyncedAt: null,
  lastSyncedAtRelative: null,
  syncType: null,
  syncResult: 'none',
  connected: false
}, {
  id: 'redmine',
  name: 'Redmine',
  iconName: 'ClipboardListIcon',
  tenantName: null,
  lastSyncedAt: null,
  lastSyncedAtRelative: null,
  syncType: null,
  syncResult: 'none',
  connected: false
}];
function ExternalIntegrationsPage() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.externalIntegrations'
  });
  useDocumentTitle(t('pageTitle'));
  return <VStack className="mx-auto w-[1000px] py-12 gap-8">
      <PageHeader />
      <VStack className="gap-6">
        <TableHeader />
        <IntegrationTable integrations={EXTERNAL_SYSTEM_INTEGRATIONS} />
      </VStack>
    </VStack>;
}
function PageHeader() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.externalIntegrations'
  });
  return <VStack className="items-start gap-1.5">
      <Text variant="h2">{t('pageTitle')}</Text>
      <Text variant="labelReg">{t('pageDescription')}</Text>
    </VStack>;
}
function TableHeader() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.externalIntegrations'
  });
  return <HStack className="justify-between items-center">
      <Text variant="h4">{t('tableHeader')}</Text>
      <ActionButton icon="SquareArrowOutUpRightIcon" iconColor="active" variant="ghost" disabled>
        <Text variant="buttonLabel">{t('addIntegrationButton')}</Text>
      </ActionButton>
    </HStack>;
}
function IntegrationTable({
  integrations
}: {
  integrations: ExternalSystemIntegration[];
}) {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.externalIntegrations'
  });
  if (integrations.length === 0) {
    return <VStack className="items-center justify-center gap-2 rounded-lg border border-neutral-muted-border bg-neutral-fill py-12">
        <Text variant="h4" className="text-neutral-muted-on-fill">
          {t('emptyTitle')}
        </Text>
        <Text variant="labelReg" className="text-neutral-muted-on-fill">
          {t('emptyDescription')}
        </Text>
      </VStack>;
  }
  return <VStack className="rounded-lg border border-neutral-muted-border bg-neutral-fill overflow-hidden">
      <Row className="bg-neutral-50/60 border-b border-neutral-muted-border">
        <Cell className="flex-[2]">
          <Text variant="labelSmall" className="text-neutral-muted-on-fill">
            {t('columns.software')}
          </Text>
        </Cell>
        <Cell>
          <Text variant="labelSmall" className="text-neutral-muted-on-fill">
            {t('columns.lastSyncedAt')}
          </Text>
        </Cell>
        <Cell>
          <Text variant="labelSmall" className="text-neutral-muted-on-fill">
            {t('columns.syncType')}
          </Text>
        </Cell>
        <Cell>
          <Text variant="labelSmall" className="text-neutral-muted-on-fill">
            {t('columns.syncResult')}
          </Text>
        </Cell>
        <Cell className="flex-none w-[120px] justify-end" />
      </Row>
      {integrations.map((integration, index) => <Row key={integration.id} className={index === integrations.length - 1 ? '' : 'border-b border-neutral-muted-border'}>
          <Cell className="flex-[2]">
            <HStack className="items-center gap-3">
              <div className="flex items-center justify-center size-8 rounded-md bg-neutral-50 border border-neutral-muted-border">
                <Icon iconName={integration.iconName} size={20} color="text-primary-on-neutral-fill" />
              </div>
              <VStack className="gap-0.5">
                <Text variant="labelSemiLarge" className="text-primary-fill">
                  {integration.name}
                </Text>
                {integration.tenantName && <HStack className="gap-1 items-center">
                    <Icon iconName="NetworkIcon" size={12} color="text-neutral-muted-on-fill" />
                    <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                      {integration.tenantName}
                    </Text>
                  </HStack>}
              </VStack>
            </HStack>
          </Cell>
          <Cell>
            {integration.lastSyncedAt ? <VStack className="gap-0.5">
                <Text variant="labelReg">{integration.lastSyncedAt}</Text>
                {integration.lastSyncedAtRelative && <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                    {integration.lastSyncedAtRelative}
                  </Text>}
              </VStack> : <Text variant="labelReg" className="text-neutral-muted-on-fill">
                -
              </Text>}
          </Cell>
          <Cell>
            {integration.syncType ? <HStack className="gap-1 items-center">
                <Icon iconName="ImportIcon" size={14} color="text-neutral-muted-on-fill" />
                <Text variant="labelReg">{integration.syncType}</Text>
              </HStack> : <Text variant="labelReg" className="text-neutral-muted-on-fill">
                -
              </Text>}
          </Cell>
          <Cell>
            <SyncResultBadge result={integration.syncResult} />
          </Cell>
          <Cell className="flex-none w-[120px] justify-end">
            <ActionButton size="sm" variant={integration.connected ? 'neutral' : 'default'} disabled>
              {integration.connected ? t('reconnectButton') : t('connectButton')}
            </ActionButton>
          </Cell>
        </Row>)}
    </VStack>;
}
function Row({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <HStack className={`items-center gap-4 px-4 py-3 ${className ?? ''}`}>
      {children}
    </HStack>;
}
function Cell({
  children,
  className
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <div className={`flex-1 flex items-center ${className ?? ''}`}>
      {children}
    </div>;
}
function SyncResultBadge({
  result
}: {
  result: SyncResult;
}) {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'tickets.externalIntegrations.syncResult'
  });
  if (result === 'success') {
    return <Badge variant="mutedSuccess">{t('success')}</Badge>;
  }
  if (result === 'partialError') {
    return <Badge variant="errorMuted">{t('partialError')}</Badge>;
  }
  return <Text variant="labelReg" className="text-neutral-muted-on-fill">
      -
    </Text>;
}